import { msg, Trans } from '@lingui/macro'

import * as React from 'react'
import { Controller, useFormContext, UseFormGetValues } from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'

import { useTrans } from '~logic/i18n/useTrans'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { InputErrorMessage } from '~ui/components/formElements/accessories/InputMessages'

import { VALIDATION_ERROR_DELAY } from '../../config/form.config'
import { PasswordInputElement } from './PasswordInputElement'

type TRepeatPasswordForm = {
  newPassword: string
  repeatedPassword: string
}

type TProps = NoChildren

/**
 * Password input used to double-check that the customer typed they new password correctly.
 *
 * The `NewPasswordInput` should also be registered in the same form
 * as this component automatically validates that the values in both inputs match.
 * The validation is triggered on on change (debounced).
 *
 * ! Needs the `FormProvider` from 'react-hook-form' to be rendered somewhere above,
 * see {@link https://react-hook-form.com/api/formprovider} for examples and details.
 */
export const RepeatPasswordInput: React.FC<TProps> = () => {
  const { trans } = useTrans()
  const { getValues, control, trigger } = useFormContext<TRepeatPasswordForm>()

  const triggerField = React.useCallback(() => {
    return trigger(`repeatedPassword`)
  }, [trigger])

  const debouncedTriggerField = useDebouncedCallback(triggerField, VALIDATION_ERROR_DELAY)

  return (
    <InputControl isRequired>
      <InputLabel>
        <Trans>Enter your new password again</Trans>
      </InputLabel>
      <Controller
        name={`repeatedPassword`}
        control={control}
        rules={{
          onChange: debouncedTriggerField,
          validate: () => {
            const validationValue = doPasswordsMatch(getValues)
            return validationValue === true ? validationValue : trans(validationValue)
          },
        }}
        render={({
          field: { onChange, onBlur, value, name },
          fieldState: { invalid, error },
          formState: { isSubmitting },
        }) => (
          <>
            <PasswordInputElement
              id={name}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              isInvalid={invalid}
              isDisabled={isSubmitting}
              autoComplete={`new-password`}
            />
            <AnimatedHeight isOpen={invalid && Boolean(error?.message)}>
              <InputErrorMessage>{error?.message ?? null}</InputErrorMessage>
            </AnimatedHeight>
          </>
        )}
      />
    </InputControl>
  )
}

// * HELPERS

// gets values of both password inputs
// returns `true` if they match, the `errorMessage` otherwise
const doPasswordsMatch = (getValues: UseFormGetValues<TRepeatPasswordForm>) => {
  const { newPassword, repeatedPassword } = getValues()

  const isSame = newPassword === repeatedPassword
  const errorMessage = msg`Passwords do not match`

  return isSame ? true : errorMessage
}
